import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle2, Download, FileUp } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Button,
    Input,
    Label,
    Textarea,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Loader,
    useToast,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/shared/components/ui";
import { MarkdownRenderer } from "@/shared/components/markdown";
import { extractErrorMessage } from "@/shared/lib/errorUtils";
import DreService from "../services/dreService";
import { MappingResponse, TabulationResponse, UserValidationRequest } from "../types";

interface DreAnalysisDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type Step = "upload" | "validate" | "result";

const MAX_SIZE_MB = 30;

function stripJsonCodeBlocks(text: string): string {
    if (!text) return "";
    let cleaned = text.replace(/```json[\s\S]*?```/g, "");
    cleaned = cleaned.replace(/```[\s\S]*?```/g, "");
    return cleaned.trim();
}

export default function DreAnalysisDialog({ open, onOpenChange }: DreAnalysisDialogProps) {
    const [step, setStep] = useState<Step>("upload");
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [mapping, setMapping] = useState<MappingResponse | null>(null);
    const [userAction, setUserAction] = useState<"prosseguir" | "reprovar">("prosseguir");
    const [correctedMapping, setCorrectedMapping] = useState("");
    const [result, setResult] = useState<TabulationResponse | null>(null);
    const { toast } = useToast();
    const { t } = useTranslation("dre");

    const tableHeaders = useMemo(() => {
        if (!result?.table_data?.length) return [];
        return Array.from(result.table_data.reduce<Set<string>>((acc, row) => {
            Object.keys(row).forEach((k) => acc.add(k));
            return acc;
        }, new Set()).values());
    }, [result?.table_data]);

    const onSelectFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        if (!e.target.files || !e.target.files[0]) return;
        const selected = e.target.files[0];
        if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
            toast({ variant: "destructive", title: t("fileTooLarge"), description: t("fileSizeExceededDesc", { maxSize: MAX_SIZE_MB }) });
            e.target.value = "";
            return;
        }
        setFile(selected);
    };

    const handleStartMapping = async () => {
        if (!file) {
            toast({ variant: "destructive", title: t("selectFile"), description: t("selectFileDesc") });
            return;
        }
        setLoading(true);
        try {
            const resp = await DreService.uploadForMapping(file);
            setMapping(resp);
            setStep("validate");
        } catch (e: unknown) {
            toast({ variant: "destructive", title: t("mappingError"), description: extractErrorMessage(e, t("uploadError")) });
        } finally {
            setLoading(false);
        }
    };

    const handleSendValidation = async () => {
        if (!mapping) return;
        const payload: UserValidationRequest = {
            action: userAction,
            corrected_mapping: userAction === "reprovar" ? correctedMapping : undefined,
            file_id: mapping.file_id,
            assistant_id: mapping.assistant_id,
            thread_id: mapping.thread_id,
            metrics_mapping: mapping.metrics_mapping,
            structure_analysis: mapping.structure_analysis || undefined,
        };
        setLoading(true);
        try {
            const resp = await DreService.sendValidation(payload);
            setResult(resp);
            setStep("result");
        } catch (e: unknown) {
            toast({ variant: "destructive", title: t("validationError"), description: extractErrorMessage(e, t("validationUploadError")) });
        } finally {
            setLoading(false);
        }
    };

    const downloadExcel = async () => {
        if (!result?.table_data || result.table_data.length === 0) return;
        const rows = result.table_data;
        try {
            const XLSX = await import('xlsx');
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'DRE');
            const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = t("exportFileName") + '.xlsx';
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            const headers = Array.from(rows.reduce<Set<string>>((acc, row) => {
                Object.keys(row).forEach((k) => acc.add(k));
                return acc;
            }, new Set()).values());
            const th = `<tr>${headers.map(h => `<th>${String(h)}</th>`).join('')}</tr>`;
            const trs = rows.map(row => `<tr>${headers.map(h => `<td>${row[h] ?? ''}</td>`).join('')}</tr>`).join('');
            const html = `<html><head><meta charset="UTF-8"></head><body><table>${th}${trs}</table></body></html>`;
            const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = t("exportFileName") + '.xls';
            a.click();
            URL.revokeObjectURL(url);
        }
    };

    const reset = () => {
        setStep("upload");
        setFile(null);
        setMapping(null);
        setCorrectedMapping("");
        setResult(null);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
            <DialogContent
                className="w-[90vw] max-w-[90vw] md:max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-[80vw] p-0 gap-0 overflow-hidden"
            >
                {/* Header com gradiente */}
                <div className="bg-gradient-to-r from-accent to-accent/90 px-6 py-5">
                    <DialogHeader className="text-white">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/20 rounded-lg">
                                <FileUp className="h-5 w-5" />
                            </div>
                            <DialogTitle className="text-lg font-semibold text-white">
                                {t("title")}
                            </DialogTitle>
                        </div>
                    </DialogHeader>
                </div>

                {/* Conteudo */}
                <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">

                    {step === "upload" && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>{t("fileLabel")}</Label>
                                <Input type="file" accept=".pdf,.xls,.xlsx,.xlsb,.xlsm,.csv" onChange={onSelectFile} />
                                <p className="text-xs text-muted-foreground">{t("fileDesc")}</p>
                            </div>
                            <Button onClick={handleStartMapping} disabled={!file || loading}>
                                {loading ? <Loader size="sm" className="mr-2" /> : <FileUp className="mr-2 h-4 w-4" />}
                                {t("startAnalysis")}
                            </Button>
                        </div>
                    )}

                    {step === "validate" && mapping && (
                        <div className="space-y-4">
                            <Tabs defaultValue="mapping">
                                <TabsList>
                                    <TabsTrigger value="mapping">{t("suggestedMapping")}</TabsTrigger>
                                    <TabsTrigger value="structure">{t("structureAnalysis")}</TabsTrigger>
                                </TabsList>
                                <TabsContent value="mapping">
                                    <div className="p-3 rounded-md bg-muted max-h-[40vh] overflow-auto">
                                        <MarkdownRenderer content={mapping.metrics_mapping} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="structure">
                                    <div className="p-3 rounded-md bg-muted max-h-[40vh] overflow-auto">
                                        <MarkdownRenderer content={mapping.structure_analysis || t("noDetails")} />
                                    </div>
                                </TabsContent>
                            </Tabs>

                            <div className="space-y-2">
                                <Label>{t("action")}</Label>
                                <div className="flex items-center gap-2">
                                    <Button variant={userAction === "prosseguir" ? "default" : "outline"} size="sm" onClick={() => setUserAction("prosseguir")}>{t("approveMapping")}</Button>
                                    <Button variant={userAction === "reprovar" ? "default" : "outline"} size="sm" onClick={() => setUserAction("reprovar")}>{t("correctMapping")}</Button>
                                </div>
                            </div>

                            {userAction === "reprovar" && (
                                <div className="space-y-2">
                                    <Label>{t("correctionTitle")}</Label>
                                    <Textarea value={correctedMapping} onChange={(e) => setCorrectedMapping(e.target.value)} placeholder={t("correctionPlaceholder")} rows={6} />
                                    <p className="text-xs text-muted-foreground">{t("correctionHint")}</p>
                                </div>
                            )}

                            <div className="flex gap-2">
                                <Button onClick={() => setStep("upload")} variant="outline">{t("back", { ns: "common" })}</Button>
                                <Button onClick={handleSendValidation} disabled={loading || (userAction === "reprovar" && !correctedMapping.trim())}>
                                    {loading ? <Loader size="sm" className="mr-2" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                                    {t("submitValidation")}
                                </Button>
                            </div>
                        </div>
                    )}

                    {step === "result" && result && (
                        <div className="space-y-4">
                            {result.error ? (
                                <div className="flex items-center gap-2 text-red-600 text-sm"><AlertCircle className="h-4 w-4" />{result.error}</div>
                            ) : (
                                <>
                                    <div className="text-sm">
                                        <p className="font-medium mb-1">{t("summary")}</p>
                                        <div className="p-3 rounded-md bg-muted max-h-[30vh] overflow-auto">
                                            <MarkdownRenderer content={stripJsonCodeBlocks(result.resposta)} />
                                        </div>
                                    </div>
                                    {tableHeaders.length > 0 && (
                                        <div className="border rounded-md overflow-auto max-h-[55vh]">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        {tableHeaders.map((h) => (
                                                            <TableHead key={h}>{h}</TableHead>
                                                        ))}
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {result.table_data!.map((row, idx) => (
                                                        <TableRow key={idx}>
                                                            {tableHeaders.map((h) => (
                                                                <TableCell key={h}>{String(row[h] ?? '')}</TableCell>
                                                            ))}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )}
                                    <div className="flex gap-2">
                                        <Button onClick={downloadExcel}><Download className="mr-2 h-4 w-4" />{t("downloadExcel")}</Button>
                                    </div>
                                </>
                            )}
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setStep("validate")}>{t("back", { ns: "common" })}</Button>
                                <Button onClick={() => onOpenChange(false)}>{t("finish")}</Button>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
